import { useState, useRef, useEffect, useCallback } from 'react';
import { debounce } from '../../utils/event-utils';

const CONTROLS_HIDE_TIMEOUT = 1500;

export const useControlsVisibility = (
  isPlaying: boolean,
  isFullscreen: boolean,
  isLoopModalOpen: boolean,
) => {
  const [showControls, setShowControls] = useState(true);
  const userInteractingRef = useRef(false);
  const mouseOverControlsRef = useRef(false);

  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMouseMoveTimeRef = useRef<number>(Date.now());

  // Show controls
  const showPlayerControls = useCallback(() => {
    setShowControls(true);

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Track user interaction start
  const handleUserInteractionStart = () => {
    userInteractingRef.current = true;
    setShowControls(true); // This will trigger re-render
    showPlayerControls();
  };

  // Hide controls if conditions allow
  const hidePlayerControls = useCallback(() => {
    if (!isLoopModalOpen && !userInteractingRef.current && !mouseOverControlsRef.current) {
      setShowControls(false);
    }
  }, [isLoopModalOpen]);

  // Reset the timeout for hiding controls
  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    const timeoutDuration = isFullscreen ? CONTROLS_HIDE_TIMEOUT * 2 : CONTROLS_HIDE_TIMEOUT;

    controlsTimeoutRef.current = setTimeout(() => {
      hidePlayerControls();
    }, timeoutDuration);
  }, [hidePlayerControls, isFullscreen]);

  // Track user interaction end
  const handleUserInteractionEnd = () => {
    userInteractingRef.current = false;
    resetControlsTimeout();
  };

  // Handle mouse movement for controls visibility
  const handleMouseMove = useCallback(
    debounce((e: MouseEvent) => {
      lastMouseMoveTimeRef.current = Date.now();
      if (!showControls) {
        showPlayerControls();
      }
    }, 50),
    [showControls, showPlayerControls],
  );

  // Set up autohide functionality
  useEffect(() => {
    showPlayerControls();

    // Clean up timeouts
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Keep controls visible during loop modal
  useEffect(() => {
    if (isLoopModalOpen) {
      showPlayerControls();
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    } else {
      resetControlsTimeout();
    }
  }, [isLoopModalOpen, showPlayerControls, resetControlsTimeout]);

  // Effect to manage controls visibility based on playing state
  useEffect(() => {
    if (isPlaying) {
      if (showControls && !userInteractingRef.current && !mouseOverControlsRef.current) {
        resetControlsTimeout();
      }
    } else {
      showPlayerControls();
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = null;
      }
    }
  }, [
    isPlaying,
    showControls,
    resetControlsTimeout,
    showPlayerControls,
    userInteractingRef,
    mouseOverControlsRef,
    controlsTimeoutRef,
  ]);

  return {
    showControls,
    userInteracting: userInteractingRef.current,
    mouseOverControls: mouseOverControlsRef.current,
    setMouseOverControls: (value: boolean) => {
      mouseOverControlsRef.current = value;
    },
    lastMouseMoveTimeRef,
    showPlayerControls,
    hidePlayerControls,
    resetControlsTimeout,
    handleUserInteractionStart,
    handleUserInteractionEnd,
    handleMouseMove,
  };
};
